import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { MachineSummary } from '../../features/machines/types/machine';
import { addRecentWork, readRecentWork, recentWorkDestination, recentWorkStorageKey, type RecentWorkItem } from './recent-work';

/** Stores only visited, access-checked machine destinations. No cross-machine API fan-out. */
export function useRecentWork(customerId: string | null, userId: string | null, machines: MachineSummary[] | undefined): RecentWorkItem[] {
  const { pathname } = useLocation();
  const [recent, setRecent] = useState<RecentWorkItem[]>([]);
  const key = customerId && userId ? recentWorkStorageKey(customerId, userId) : null;

  useEffect(() => {
    if (!key) {
      setRecent([]);
      return;
    }
    let saved: RecentWorkItem[] = [];
    try {
      saved = readRecentWork(localStorage, key);
    } catch {
      // Storage may be unavailable. Navigation must still work.
    }
    // Never display a stale link for a machine outside the current accessible directory.
    const available = new Map((machines ?? []).map((machine) => [machine.id, machine]));
    const visible = saved.filter((item) => available.has(item.machineId))
      .map((item) => ({ ...item, machineName: available.get(item.machineId)!.name }));
    const route = recentWorkDestination(pathname);
    const machine = route ? available.get(route.machineId) : null;
    // Detail pages normally expose a readable title. Use that when available;
    // otherwise fall back to the work type rather than exposing database IDs.
    const pageHeading = route && ['Investigation', 'Conversation', 'Document'].includes(route.title)
      ? document.querySelector<HTMLElement>('.r42-main h1')?.textContent?.trim()
      : null;
    const title = pageHeading && pageHeading.length <= 120 && !/^(loading|opening)/i.test(pageHeading)
      ? pageHeading : route?.title;
    const next = route && machine && title
      ? addRecentWork(visible, { href: pathname, title, machineId: machine.id, machineName: machine.name })
      : visible;
    setRecent(next);
    if (route && machine) {
      try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* Best effort only. */ }
    }
  }, [key, pathname, machines]);

  return recent;
}
